import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faLock, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [showTotpInput, setShowTotpInput] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') || '/';

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate(redirectTo, { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate, redirectTo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(username, password, totp || null);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      const twoFactorRequired = !!err.response?.data?.twoFactorRequired;
      setShowTotpInput(twoFactorRequired);
      setError(err.response?.data?.error || err.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const fieldClass =
    'block w-full pl-10 pr-3 h-11 lt-input text-sm placeholder:text-muted/70 disabled:opacity-50';

  return (
    <div className="min-h-screen flex items-center justify-center bg-canvas px-4">
      <div className="w-full max-w-[420px]">
        <div className="mb-8">
          <div className="text-[11px] uppercase tracking-[0.22em] text-muted mb-2">LionTrading</div>
          <h1 className="text-[28px] font-semibold tracking-tight text-ink">Sign in to the desk</h1>
          <p className="mt-1 text-sm text-muted">Secure access to live markets, blotters, and algorithms.</p>
        </div>

        <div className="lt-card p-6">
          {error && (
            <div className="mb-5 px-3 py-2.5 rounded-md border border-[rgba(240,113,103,0.35)] bg-[var(--lt-bear-dim)] text-bear text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="username" className="lt-label mb-2 block">Username</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted">
                  <FontAwesomeIcon icon={faUser} className="text-xs" />
                </div>
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  className={fieldClass}
                  placeholder="Username"
                  disabled={loading}
                  autoComplete="username"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="lt-label mb-2 block">Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted">
                  <FontAwesomeIcon icon={faLock} className="text-xs" />
                </div>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className={fieldClass}
                  placeholder="Password"
                  disabled={loading}
                  autoComplete="current-password"
                />
              </div>
            </div>

            {showTotpInput && (
              <div>
                <label htmlFor="totp" className="lt-label mb-2 block">Authenticator code</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted">
                    <FontAwesomeIcon icon={faLock} className="text-xs" />
                  </div>
                  <input
                    id="totp"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={totp}
                    onChange={(e) => setTotp(e.target.value.replace(/[^0-9]/g, ''))}
                    className={`${fieldClass} num tracking-[0.28em]`}
                    placeholder="000000"
                    disabled={loading}
                    autoComplete="one-time-code"
                  />
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-md bg-action text-white font-semibold text-sm hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
                  Authenticating
                </>
              ) : (
                'Continue'
              )}
            </button>
          </form>
        </div>

        <p className="mt-4 text-xs text-muted">
          Accounts are provisioned by an administrator. 2FA is required off trusted networks.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
